<template>
    <Teleport to="body">
        <div class="check-backdrop" @click.self="emit('close')">
            <div class="check-dialog" role="dialog" aria-modal="true" :aria-label="`Check ${heading}`">
                <header class="check-header">
                    <div class="title-block">
                        <div class="title">Template check — {{ heading }}</div>
                        <div class="subline">Columns of the uploaded file compared with the validator rules and the goAML XML builder for {{ type }}</div>
                    </div>
                    <button class="close" title="Close" aria-label="Close" @click="emit('close')">✕</button>
                </header>

                <div v-if="loading" class="state"><LoaderItem size="24px" /></div>
                <div v-else-if="!result" class="state">The template could not be checked.</div>
                <div v-else class="check-body">
                    <div v-if="!result.supported" class="callout bad">There is no XML report builder for this template type, so files of this type can't be turned into goAML XML.</div>
                    <div v-if="!result.hasFile" class="callout bad">No template file is uploaded, so only the validator fields are listed.</div>

                    <div class="chips">
                        <span class="chip ok">{{ result.summary.ok }} validated and in XML</span>
                        <span class="chip warn" :class="{ zero: !result.summary.validatorOnly }">{{ result.summary.validatorOnly }} validated but not in XML</span>
                        <span class="chip muted" :class="{ zero: !result.summary.unused }">{{ result.summary.unused }} ignored</span>
                        <span class="chip bad" :class="{ zero: !result.summary.missing }">{{ result.summary.missing }} required but missing</span>
                        <span v-if="result.summary.duplicate" class="chip bad">{{ result.summary.duplicate }} duplicate</span>
                    </div>

                    <section v-if="result.missing.length">
                        <h3>Validator fields not in the file</h3>
                        <table class="check-table">
                            <thead><tr><th>Field</th><th>Rule</th><th>Effect</th></tr></thead>
                            <tbody>
                                <tr v-for="field in result.missing" :key="field.name">
                                    <td class="name">{{ field.name }}</td>
                                    <td><code v-if="field.validator">{{ field.validator }}</code></td>
                                    <td>
                                        <span v-if="field.optional" class="status muted">Optional: always empty{{ field.usedBy.length ? ' in the XML' : '' }}</span>
                                        <span v-else class="status bad">Every row fails validation</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section v-if="result.columns.length">
                        <div class="section-head">
                            <h3>Columns in the file</h3>
                            <label class="filter"><input type="checkbox" v-model="onlyIssues"> Only columns not in the XML</label>
                        </div>
                        <table class="check-table">
                            <thead><tr><th>Col</th><th>Header</th><th>Rule</th><th>Status</th></tr></thead>
                            <tbody>
                                <tr v-for="col in shownColumns" :key="col.index">
                                    <td class="letter">{{ col.letter }}</td>
                                    <td class="name">
                                        {{ col.name }}
                                        <div v-if="col.group" class="group">{{ col.group }}</div>
                                    </td>
                                    <td>
                                        <code v-if="col.validator">{{ col.validator }}</code>
                                        <span v-if="col.optional" class="optional">optional</span>
                                    </td>
                                    <td>
                                        <span class="status" :class="STATUS[col.status].tone" :title="col.usedBy.length ? `Read by: ${col.usedBy.join(', ')}` : ''">
                                            {{ STATUS[col.status].label }}
                                        </span>
                                        <div class="explain">{{ STATUS[col.status].text }}</div>
                                    </td>
                                </tr>
                                <tr v-if="!shownColumns.length"><td colspan="4" class="empty">Every column is validated and written to the XML.</td></tr>
                            </tbody>
                        </table>
                    </section>

                    <p class="footnote">
                        "In XML" means the report builder for this type reads the field somewhere; some fields are only
                        written for certain rows (e.g. a given Role or Transfer Type). Hover a status to see which parts of the builder read it.
                    </p>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
    import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
    import BO_API from '@/api/bo_api.js';
    import LoaderItem from '@/components/LoaderItem.vue';

    const props = defineProps({
        type: { type: String, required: true },
        title: { type: String, default: '' },
    });
    const emit = defineEmits(['close']);

    const STATUS = {
        ok: { label: 'In XML', tone: 'ok', text: 'Validated and written to the goAML XML.' },
        validator_only: { label: 'Validated, not in XML', tone: 'warn', text: 'Checked on upload (can reject rows) but this report type never writes it to the XML.' },
        unused: { label: 'Ignored', tone: 'muted', text: 'Not validated and not written to the XML; removing it changes nothing in the reports.' },
        duplicate: { label: 'Duplicate header', tone: 'bad', text: 'Another column has the same header; only the last one is read.' },
    };

    const result = ref(null);
    const loading = ref(true);
    const onlyIssues = ref(false);

    const heading = computed(() => props.title || props.type);
    const shownColumns = computed(() => (result.value?.columns || []).filter(col => !onlyIssues.value || col.status !== 'ok'));

    const onKeydown = (event) => { if (event.key === 'Escape') emit('close'); };
    onMounted(async () => {
        document.addEventListener('keydown', onKeydown);
        result.value = await BO_API.getTemplateCheck(props.type);
        loading.value = false;
    });
    onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<style scoped lang="scss">
.check-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1500;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    box-sizing: border-box;
}
.check-dialog {
    --muted: #5f6368;
    background: #fff;
    border-radius: 12px;
    width: min(900px, 100%);
    max-height: calc(100vh - 32px);
    display: flex;
    flex-direction: column;
    text-align: left;
    overflow: hidden;
}
.check-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-color, #e3e5ea);
}
.title-block { flex: 1; min-width: 0; }
.title { font-size: 18px; color: #1f1f1f; }
.subline { font-size: 13px; color: var(--muted); margin-top: 4px; }
.close {
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--muted);
    font-size: 16px;
    cursor: pointer;
    flex: none;
    &:hover { background: #eef1f7; }
}
.state { padding: 60px; display: flex; justify-content: center; color: var(--muted); }
.check-body {
    padding: 16px 20px 20px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.callout {
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 14px;
    &.bad { background: #fce8e6; border: 1px solid #f4b4ae; }
}
.chips { display: flex; flex-wrap: wrap; gap: 8px; }
.chip {
    font-size: 13px;
    padding: 4px 10px;
    border-radius: 12px;
    &.zero { opacity: 0.5; }
}
h3 { font-size: 15px; margin: 0 0 8px; }
.section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    h3 { margin: 0 0 8px; }
}
.filter { font-size: 13px; color: var(--muted); display: flex; align-items: center; gap: 6px; cursor: pointer; }
.check-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    th {
        text-align: left;
        font-weight: 600;
        color: var(--muted);
        font-size: 12px;
        padding: 6px 8px;
        border-bottom: 1px solid var(--border-color, #e3e5ea);
    }
    td {
        padding: 8px;
        border-bottom: 1px solid #f0f1f4;
        vertical-align: top;
    }
    .letter { color: var(--muted); width: 36px; }
    .name { font-weight: 500; }
    .group { font-size: 12px; color: var(--muted); font-weight: 400; }
    .empty { color: var(--muted); text-align: center; }
}
code { font-size: 12px; background: #f1f3f6; padding: 1px 5px; border-radius: 4px; }
.optional { font-size: 12px; color: var(--muted); margin-left: 6px; }
.status {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
}
.explain { font-size: 12px; color: var(--muted); margin-top: 3px; }
.ok { background: #e6f4ea; color: #137333; }
.warn { background: #fef7e0; color: #8a5a00; }
.muted { background: #f1f3f4; color: #5f6368; }
.bad { background: #fce8e6; color: #b3261e; }
.footnote { font-size: 12px; color: var(--muted); margin: 0; }
</style>
